import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { GearPal, GearItem } from "../../types/Pal";
import GearHeadings from "../../ui/gearheadings";
import GearButton from "../../ui/gearbutton";
import {
  IoChevronBack,
  IoChevronForward,
  IoMusicalNotes,
  IoTrashOutline,
  IoEyeOutline,
} from "react-icons/io5";

interface UserGearItemsProps {
  gearpalsData: GearPal[];
  onEditItem?: (item: GearItem) => void;
  onDeleteItem?: (id: number) => void;
  onViewItem?: (item: GearItem, pal: GearPal) => void;
}

export default function UserGearItems({
  gearpalsData,
  onEditItem,
  onDeleteItem,
  onViewItem,
}: UserGearItemsProps) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Since user has only one GearPal usually, we take the first one
  const palToEdit = gearpalsData?.[0];
  const items = palToEdit?.items || [];

  if (!palToEdit) return null;

  const nextItems = () => {
    if (currentIndex + 2 < items.length) {
      setCurrentIndex(currentIndex + 2);
    }
  };

  const prevItems = () => {
    if (currentIndex - 2 >= 0) {
      setCurrentIndex(currentIndex - 2);
    }
  };

  const visibleItems = items.slice(currentIndex, currentIndex + 2);

  return (
    <div className="mt-8 profile-card w-full pt-6 relative ">
      <div className="flex justify-between items-center mb-6 w-full">
        <GearHeadings variant="h3" className="text-secondary !mb-0">
          {t("pages.profile.yourGear", "La tua attrezzatura")}
        </GearHeadings>

        {items.length > 2 && (
          <div className="flex gap-2">
            <button
              onClick={prevItems}
              disabled={currentIndex === 0}
              className="p-2 rounded-full bg-white/5 text-white disabled:opacity-20 hover:bg-white/10 transition-colors border border-white/10"
            >
              <IoChevronBack size={20} />
            </button>
            <button
              onClick={nextItems}
              disabled={currentIndex + 2 >= items.length}
              className="p-2 rounded-full bg-white/5 text-white disabled:opacity-20 hover:bg-white/10 transition-colors border border-white/10"
            >
              <IoChevronForward size={20} />
            </button>
          </div>
        )}
      </div>

      {/* Grid of Items */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
        {visibleItems.map((item, idx) => (
          <GearItemCard
            key={idx}
            item={item}
            city={palToEdit.city}
            region={palToEdit.region}
            onEdit={() => onEditItem?.(item)}
            onDelete={() => item.id && onDeleteItem?.(item.id)}
            onView={() => onViewItem?.(item, palToEdit)}
          />
        ))}

        {items.length === 0 && (
          <p className="text-white/40 text-center py-8 italic col-span-2">
            {t("pages.profile.noGear", "Nessuna attrezzatura aggiunta.")}
          </p>
        )}
      </div>

      {/* Optional: Pagination indicators */}
      {items.length > 2 && (
        <div className="flex justify-center gap-2 mt-6 mx-auto">
          {Array.from({ length: Math.ceil(items.length / 2) }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                Math.floor(currentIndex / 2) === i
                  ? "w-8 bg-secondary"
                  : "w-2 bg-white/20"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function GearItemCard({
  item,
  city,
  region,
  onEdit,
  onDelete,
  onView,
}: {
  item: GearItem;
  city: string;
  region: string;
  onEdit: () => void;
  onDelete: () => void;
  onView: () => void;
}) {
  const { t } = useTranslation();
  const imageUrl = item.images?.[0]?.linkImage || item.imageUrls?.[0];

  return (
    <div className="bg-[#2a1b41] border border-white/10 rounded-[28px] overflow-hidden flex flex-col hover:border-white/20 transition-all group">
      {/* Image Container */}
      <div className="p-2 pb-0">
        <div className="relative h-44 w-full rounded-[20px] overflow-hidden bg-white/5 flex items-center justify-center">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={item.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="flex flex-col items-center gap-2 text-white/20">
              <IoMusicalNotes size={48} />
              <span className="text-xs font-medium uppercase tracking-wider">
                {item.category}
              </span>
            </div>
          )}
          {/* Subtle overlay for better text contrast if needed */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />

          {/* Delete Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="absolute top-3 right-3 p-2 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white rounded-xl backdrop-blur-md border border-red-500/20 transition-all z-10"
            title={t("common.delete", "Elimina")}
          >
            <IoTrashOutline size={18} />
          </button>
        </div>
      </div>

      {/* Details Container */}
      <div className="p-5 flex flex-col flex-grow">
        <div className="flex justify-between items-start mb-1">
          <h4 className="text-white font-bold text-lg leading-tight line-clamp-1">
            {item.name}
          </h4>
          <span className="text-secondary font-semibold text-base whitespace-nowrap ml-2">
            {item.noleggio !== "0.00"
              ? `€${item.noleggio}${t("common.perDay", "/day")}`
              : t("common.free", "Free")}
          </span>
        </div>

        <p className="text-white/40 text-sm mb-6">
          {city}, {region}
        </p>

        <div className="flex gap-3 mt-auto">
          <GearButton
            variant="secondary"
            onClick={onEdit}
            className="flex-1 !rounded-full font-bold shadow-lg shadow-secondary/20 hover:shadow-secondary/30 transition-all active:scale-95"
          >
            {t("common.edit", "Edit")}
          </GearButton>
          <GearButton
            variant="primary"
            onClick={onView}
            className="flex-1 !rounded-full font-bold bg-[#5A18C4] !border-none shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all active:scale-95"
          >
            <div className="flex items-center gap-2">
              <IoEyeOutline size={18} />
              {t("booking.view", "Visualizza")}
            </div>
          </GearButton>
        </div>
      </div>
    </div>
  );
}
